import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../components/ui/FoxSpinner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Crown, Trophy, Star, Sparkles, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

// Componente Carrossel de Participantes
const ParticipantsCarousel = ({ participants }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerPage = 6; // 2 linhas de 3
  const totalPages = Math.ceil(participants.length / itemsPerPage);

  const nextPage = () => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const currentParticipants = participants.slice(
    currentIndex * itemsPerPage,
    (currentIndex + 1) * itemsPerPage
  );

  return (
    <div className="relative">
      {/* Botões de Navegação */}
      <div className="flex items-center justify-center gap-4 mb-8">
        <Button
          onClick={prevPage}
          variant="outline"
          className="bg-white/90 hover:bg-white border-2 border-purple-400"
          disabled={participants.length <= itemsPerPage}
        >
          <ChevronLeft className="w-6 h-6" />
        </Button>
        
        <span className="text-white font-bold text-lg">
          Página {currentIndex + 1} de {totalPages}
        </span>
        
        <Button
          onClick={nextPage}
          variant="outline"
          className="bg-white/90 hover:bg-white border-2 border-purple-400"
          disabled={participants.length <= itemsPerPage}
        >
          <ChevronRight className="w-6 h-6" />
        </Button>
      </div>

      {/* Grid de Participantes */}
      <div className="grid md:grid-cols-3 gap-6 min-h-[400px]">
        {currentParticipants.map((p, idx) => (
          <Card key={p.id} className="bg-white/95 hover:shadow-2xl transition-all hover:scale-105">
            <CardContent className="p-6 text-center">
              {p.kako_avatar && (
                <img 
                  src={p.kako_avatar} 
                  alt={p.kako_nickname} 
                  className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-purple-300"
                />
              )}
              <h3 className="font-bold text-xl mb-2">{p.kako_nickname}</h3>
              <Badge className="bg-purple-600 text-lg px-4 py-1">
                {p.total_votes} votos
              </Badge>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Indicadores de Página */}
      <div className="flex justify-center gap-2 mt-6">
        {[...Array(totalPages)].map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIndex(idx)}
            className={`w-3 h-3 rounded-full transition-all ${
              idx === currentIndex 
                ? 'bg-white w-8' 
                : 'bg-white/50 hover:bg-white/80'
            }`}
          />
        ))}
      </div>
      
      {/* Total */}
      <p className="text-center text-white mt-6 text-lg">
        Total: {participants.length} participantes
      </p>
    </div>
  );
};

const MeuHostPreferidoPublic = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [participants, setParticipants] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const [configRes, participantsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/host-event/config`),
        axios.get(`${BACKEND_URL}/api/host-event/participants`)
      ]);
      
      setConfig(configRes.data);
      setParticipants(participantsRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <FoxSpinnerInline size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-800 to-pink-700">
      {/* Header com Efeito */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 via-purple-500/20 to-pink-600/20 animate-pulse"></div>
        
        <div className="relative max-w-6xl mx-auto px-4 py-12 sm:py-16 text-center">
          {/* Título Principal */}
          <div className="mb-8">
            <Crown className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-0 text-yellow-300 drop-shadow-[0_0_30px_rgba(253,224,71,0.8)] animate-bounce" />
            
            {/* Imagem do Título */}
            <img 
              src="https://customer-assets.emergentagent.com/job_rewards-admin-1/artifacts/jhwsatgv_IMG_8431.PNG"
              alt="Meu Host Preferido"
              className="max-w-full h-auto mx-auto mb-4 drop-shadow-[0_0_30px_rgba(255,255,255,0.8)] -mt-4"
              style={{ maxHeight: '250px' }}
            />
            
            <p className="text-base sm:text-xl md:text-2xl text-blue-100 font-semibold mb-4 sm:mb-6 px-4">
              Vote no seu host favorito e ajude-o conquistar o prêmio!
            </p>
            
            {/* Informações de Participação */}
            <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto mb-8">
              <div className="bg-gradient-to-br from-blue-100 to-purple-100 backdrop-blur-sm rounded-lg p-4 border-2 border-blue-400 shadow-lg">
                <h3 className="text-lg font-bold text-blue-900 mb-2">👑 HOSTS</h3>
                <p className="text-sm text-gray-800">
                  Se inscrevem para competir pelo grande prêmio!
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-pink-100 to-purple-100 backdrop-blur-sm rounded-lg p-4 border-2 border-pink-400 shadow-lg">
                <h3 className="text-lg font-bold text-pink-900 mb-2">💎 PATROCINADORES</h3>
                <p className="text-sm text-gray-800">
                  Recarregam moedas e ganham votos para ajudar seu host favorito!
                </p>
              </div>
            </div>
          </div>

          {/* Prêmio em Destaque */}
          <Card className="max-w-2xl mx-auto bg-gradient-to-br from-yellow-100 to-orange-100 border-4 border-yellow-400 shadow-[0_0_50px_rgba(234,179,8,0.5)]">
            <CardContent className="p-4 sm:p-8">
              <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <Trophy className="w-8 h-8 sm:w-12 sm:h-12 text-yellow-600" />
                <h2 className="text-2xl sm:text-3xl font-black text-yellow-900">PRÊMIO</h2>
                <Trophy className="w-8 h-8 sm:w-12 sm:h-12 text-yellow-600" />
              </div>
              <p className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-orange-600 animate-pulse break-words">
                {config.prize_description}
              </p>
            </CardContent>
          </Card>

          {/* Botão CTA Principal */}
          <Button
            onClick={() => navigate('/')}
            className="mt-8 px-6 sm:px-12 py-4 sm:py-6 text-lg sm:text-2xl md:text-3xl font-black bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-[0_0_30px_rgba(34,197,94,0.6)] hover:shadow-[0_0_50px_rgba(34,197,94,0.8)] transform hover:scale-105 transition-all w-full sm:w-auto"
          >
            <Sparkles className="mr-2 sm:mr-3 w-5 h-5 sm:w-8 sm:h-8" />
            PARTICIPE AGORA!
            <ArrowRight className="ml-2 sm:ml-3 w-5 h-5 sm:w-8 sm:h-8" />
          </Button>
        </div>
      </div>

      {/* Como Funciona */}
      <div className="bg-white/10 backdrop-blur-sm py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-black text-center text-white mb-12">
            ✨ Como Funciona?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="bg-white/95">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <span className="text-3xl">1️⃣</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Inscreva-se</h3>
                <p className="text-gray-600">
                  Hosts podem se inscrever durante o período de inscrições
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/95">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                  <span className="text-3xl">2️⃣</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Vote</h3>
                <p className="text-gray-600">
                  A cada 10.000 moedas compradas você ganha 1 voto para dar ao seu favorito!
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/95">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center">
                  <span className="text-3xl">3️⃣</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Ganhe!</h3>
                <p className="text-gray-600">
                  O host mais votado leva o prêmio incrível!
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Regras */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <Card className="bg-white/95">
          <CardContent className="p-8">
            <h2 className="text-3xl font-black text-center text-purple-900 mb-6 flex items-center justify-center gap-2">
              <Star className="text-yellow-500" />
              REGRAS DO EVENTO
              <Star className="text-yellow-500" />
            </h2>
            <div className="prose prose-lg max-w-none whitespace-pre-wrap text-gray-700">
              {config.rules_text}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Participantes - Carrossel */}
      {participants.length > 0 && (
        <div className="bg-white/10 backdrop-blur-sm py-16">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-4xl font-black text-center text-white mb-12 flex items-center justify-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_rewards-admin-1/artifacts/cg441vjz_IMG_8432.PNG"
                alt="Pinguim"
                className="h-16 sm:h-20 w-auto object-contain"
              />
              Participantes
              <img 
                src="https://customer-assets.emergentagent.com/job_rewards-admin-1/artifacts/cg441vjz_IMG_8432.PNG"
                alt="Pinguim"
                className="h-16 sm:h-20 w-auto object-contain"
              />
            </h2>
            
            <ParticipantsCarousel participants={participants} />
          </div>
        </div>
      )}

      {/* CTA Final */}
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <Card className="bg-gradient-to-br from-pink-100 to-purple-100 border-4 border-pink-300">
          <CardContent className="p-12">
            <h2 className="text-4xl font-black text-purple-900 mb-6">
              Não Perca Tempo!
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Vote agora no seu host favorito e participe deste evento incrível!
            </p>
            <Button
              onClick={() => navigate('/')}
              className="px-8 sm:px-16 py-6 sm:py-8 text-xl sm:text-2xl md:text-3xl font-black bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-[0_0_40px_rgba(168,85,247,0.6)] hover:shadow-[0_0_60px_rgba(168,85,247,0.8)] transform hover:scale-105 transition-all w-full sm:w-auto"
            >
              <Crown className="mr-2 sm:mr-4 w-6 h-6 sm:w-10 sm:h-10 text-yellow-300" />
              ENTRE AGORA E PARTICIPE!
              <Star className="ml-2 sm:ml-4 w-6 h-6 sm:w-10 sm:h-10 text-yellow-300" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="text-center py-8 text-white/80">
        <p className="text-sm">
          © FOX SELLER - Evento Meu Host Preferido
        </p>
      </div>
    </div>
  );
};

export default MeuHostPreferidoPublic;
